import React, { useEffect, useRef, useState } from "react";
import { candidateAPI } from "../services/api";
import "./RegistrationConfirmation.css";

export default function RegistrationConfirmation({ candidate, emailSent, subjects, onClose }) {
  const dialog = useRef(null);
  const closeButton = useRef(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(emailSent === true);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, []);

  const handleKeyDown = event => {
    if (event.key === "Escape") onClose();
    if (event.key !== "Tab") return;
    const buttons = dialog.current.querySelectorAll("button:not(:disabled)");
    const first = buttons[0], last = buttons[buttons.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  const resend = async () => {
    if (sending) return;
    setSending(true);
    setFeedback("");
    setError("");
    try {
      await candidateAPI.resendRegistrationEmail(candidate.email);
      setSent(true);
      setFeedback("Your registration email has been resent successfully. Please check your inbox and spam folder.");
    } catch (err) {
      setError(err?.errors?.[0]?.message || err?.message || "Unable to resend your email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const fullName = [candidate.firstname, candidate.middlename, candidate.lastname].filter(Boolean).join(" ");
  const dateOfBirth = candidate.dob ? String(candidate.dob).slice(0, 10) : "—";
  const subjectNames = (candidate.subjects || []).map(code => subjects[code] || `Subject ${code}`).join(", ");

  return (
    <div className="registration-confirmation-backdrop">
      <section ref={dialog} className="registration-confirmation" role="dialog" aria-modal="true" aria-labelledby="registration-confirmation-title" onKeyDown={handleKeyDown}>
        <header className="confirmation-header">
          <h2 id="registration-confirmation-title">Registration complete</h2>
          <button ref={closeButton} type="button" aria-label="Close registration confirmation" onClick={onClose}>×</button>
        </header>
        <div className="confirmation-body">
          <p>Your registration was successful. Save your registration number for your exam.</p>
          <div className="confirmation-number"><span>Registration number</span><strong>{candidate.regNo}</strong></div>
          <dl className="confirmation-details">
            {[
              ["Full name", fullName], ["Email address", candidate.email], ["Phone number", candidate.phone],
              ["School / institution", candidate.school], ["Date of birth", dateOfBirth],
              ["Country", candidate.country], ["State", candidate.state], ["Local government area", candidate.lga],
              ["Address", candidate.address], ["Subjects", subjectNames]
            ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || "—"}</dd></div>)}
          </dl>
          <div className="confirmation-email">
            <p>{sent
              ? <>Your registration number has also been sent to <strong>{candidate.email}</strong>. Check your inbox and spam folder.</>
              : emailSent === false
                ? <>Your registration is complete, but we could not send the email to <strong>{candidate.email}</strong>. Keep your registration number above and use Resend Email to try again.</>
                : <>Check <strong>{candidate.email}</strong> for your registration email. If it has not arrived, use Resend Email below.</>}
            </p>
            <button type="button" className="resend-btn" onClick={resend} disabled={sending}>{sending ? "Sending…" : "Resend Email"}</button>
            {feedback && <p role="status" className="confirmation-success">{feedback}</p>}
            {error && <p role="alert" className="confirmation-error">{error}</p>}
          </div>
          <button type="button" className="confirmation-done" onClick={onClose}>Done</button>
        </div>
      </section>
    </div>
  );
}
